import {useMemo, useRef} from 'react';
import isEqual from 'lodash/isEqual';
import type {DataLoadStatus} from 'api/types';
import type {Lock} from 'services/locks/types';
import {Configuration} from 'configuration';
import {useGetLockQuery} from 'services/locks';

const LOCK_REFRESH_INTERVAL_MS = 5 * 1000;

export const useCurrentLock = (
  workId: string,
  _mock?: boolean,
): DataLoadStatus<Lock | undefined> => {
  const resultRef = useRef<DataLoadStatus<Lock | undefined>>({
    status: 'NotLoaded',
  });

  const {data, hasLoaded} = useGetLockQuery(
    {contextId: workId, contextType: 'work'},
    {
      pollingInterval: Configuration.interval.lock ?? LOCK_REFRESH_INTERVAL_MS,
      // limits re-renders to actual lock changes (not status, etc).
      selectFromResult: ({data, fulfilledTimeStamp}) => ({
        data,
        hasLoaded: fulfilledTimeStamp !== undefined,
      }),
    },
  );

  return useMemo(() => {
    if (hasLoaded) {
      const newLock: DataLoadStatus<Lock | undefined> = {
        status: 'Loaded',
        data,
      };

      if (!isEqual(resultRef.current, newLock)) {
        resultRef.current = newLock;
      }
    }

    return resultRef.current;
  }, [hasLoaded, data]);
};
